import * as React from "react";
import { createRoot, Root } from "react-dom/client";
import { Doughnut } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { DashboardController } from "../../../controllers";
import NumberFormatter from "../../../utilities/NumberFormatter";
import GenericTooltip from "../../Tooltips/GenericTooltip";
import AccountReceivableLegendItem from "../AccountReceivableLegendItem";
import PageLoader from "../../Page/PageLoader";

interface AccountReceivable {
  total: number;
  contact_id: number;
  contact_name: string;
  color: string;
}

const tooltipRoots = new WeakMap<HTMLElement, Root>();

interface IProps {
  currency: string;
  numberFormat: string;
}

const AccountReceivablesWidget = (props: IProps) => {
  const { currency, numberFormat } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [accountReceivables, setAccountReceivables] = React.useState<AccountReceivable[]>([]);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await DashboardController.getAccountReceivables();
      setAccountReceivables(response);
    } catch (error) {
      console.error("Failed to fetch account receivables:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const calculateSlicePercentage = (total: number, amount: number) => {
    if (!total) return (0).toFixed(1);

    return ((amount / total) * 100).toFixed(1);
  };

  if (isLoading) {
    return (
      <div className="panel h-full flex flex-col mb-0!">
        <div className="panel-header space-between">
          {t("Dashboard::Turnover")}
          <span className="panel-header-subtext">{t("Dashboard::This year")}</span>
        </div>
        <div className="panel-content is-large min-h-0 flex-1 overflow-auto">
          <PageLoader />
        </div>
      </div>
    );
  }

  const labels = accountReceivables.map((receivable) => receivable.contact_name);
  const data = accountReceivables.map((receivable) => receivable.total);
  const backgroundColors = accountReceivables.map((receivable) => receivable.color);
  const accountReceivableTotal = accountReceivables
    .map((receivable) => receivable.total)
    .reduce((total, number) => Number(total) + Number(number), 0);
  // A doughnut can't draw slices that are all zero, so fall back to the empty placeholder ring
  const hasChartData = labels.length > 0 && accountReceivableTotal > 0;

  return (
    <div className="panel h-full flex flex-col mb-0!">
      <div className="panel-header space-between">
        {t("Dashboard::Turnover")}
        <span className="panel-header-subtext">{t("Dashboard::This year")}</span>
      </div>

      <div className="panel-content is-large min-h-0 flex-1 overflow-auto">
        <div className="flex h-full gap-6">
          <div className="relative min-h-40 min-w-0 flex-1">
            {!hasChartData && (
              <Doughnut
                data={{
                  labels: [],
                  datasets: [
                    {
                      data: [100],
                      backgroundColor: ["#9b9b9b"],
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutoutPercentage: 56,
                  legend: {
                    display: false,
                  },
                  elements: {
                    arc: {
                      borderWidth: 0,
                    },
                  },
                  tooltips: false,
                }}
              />
            )}
            {hasChartData && (
              <Doughnut
                data={{
                  labels: labels,
                  datasets: [
                    {
                      data: data,
                      backgroundColor: backgroundColors,
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  cutoutPercentage: 56,
                  legend: {
                    display: false,
                  },
                  elements: {
                    arc: {
                      borderWidth: 0,
                    },
                  },
                  tooltips: {
                    mode: "index",
                    enabled: false,
                    callbacks: {
                      label: (tooltipItem: any, chartData: any) => {
                        const dataset = chartData.datasets[tooltipItem.datasetIndex];
                        const title = chartData.labels[tooltipItem.index];
                        const total = dataset.data[tooltipItem.index];
                        const backgroundColor = dataset.backgroundColor[tooltipItem.index];
                        const percentage = calculateSlicePercentage(accountReceivableTotal, parseFloat(total));

                        return `<div class='legend-item is-receivable' >
                          <div class='legend-item-circle' style='background-color: ${backgroundColor};'></div>
                          <div class='legend-item-content'>
                            <span class='legend-item-title'>${title}</span>
                            <div>
                              <span style="font-size: 12px; color: #424955;">
                                ${NumberFormatter.formatCurrency(currency, numberFormat, total)}
                              </span>
                              <span style="font-size: 11px; color: #858C99; margin-left: 4px">
                                ${percentage}%
                              </span>
                            </div>
                          </div>
                        </div>
                        `;
                      },
                    },
                    custom: function (tooltipModel: any) {
                      const id = "accountReceivableTooltip";
                      let tooltipEl = document.getElementById(id);

                      if (!tooltipEl) {
                        tooltipEl = document.createElement("div");
                        tooltipEl.id = id;
                        tooltipEl.className = "chart-tooltip";
                        document.body.appendChild(tooltipEl);
                      }

                      if (tooltipModel.opacity === 0) {
                        // @ts-ignore
                        tooltipEl.style.opacity = 0;
                        return;
                      }

                      tooltipEl.classList.remove("above", "below", "no-transform");
                      if (tooltipModel.yAlign) {
                        tooltipEl.classList.add(tooltipModel.yAlign);
                      } else {
                        tooltipEl.classList.add("no-transform");
                      }

                      if (tooltipModel.body) {
                        let root = tooltipRoots.get(tooltipEl);
                        if (!root) {
                          root = createRoot(tooltipEl);
                          tooltipRoots.set(tooltipEl, root);
                        }
                        root.render(<GenericTooltip tooltip={tooltipModel} />);
                      }

                      // @ts-ignore
                      const position = this._chart.canvas.getBoundingClientRect();

                      // @ts-ignore
                      tooltipEl.style.opacity = 1;
                      tooltipEl.style.position = "absolute";
                      tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + "px";
                      tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + "px";
                      tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
                      tooltipEl.style.fontSize = tooltipModel.bodyFontSize + "px";
                      tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
                      tooltipEl.style.padding = tooltipModel.yPadding + "px " + tooltipModel.xPadding + "px";
                      tooltipEl.style.pointerEvents = "none";
                    },
                  },
                }}
              />
            )}
          </div>

          <div className="min-w-0 flex-1">
            <div className="dashboard-expenses-legend">
              <h1
                className="dashboard-expenses-total"
                dangerouslySetInnerHTML={{
                  __html: `${NumberFormatter.formatCurrency(currency, numberFormat, accountReceivableTotal)}`,
                }}
              />
              <div className="legend-items">
                {accountReceivables.slice(0, 5).map((accountReceivable, index) => {
                  const { color, contact_name, total } = accountReceivable;
                  const percentage = calculateSlicePercentage(accountReceivableTotal, total);

                  return (
                    <AccountReceivableLegendItem
                      key={`${contact_name}-${index}`}
                      name={contact_name}
                      total={String(total)}
                      color={color}
                      percentage={percentage}
                      currency={currency}
                      numberFormat={numberFormat}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountReceivablesWidget;
